import React from 'react';
import { Check, Star } from 'lucide-react';
import { services } from '../mock';

const Pricing = () => {
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="pricing" className="py-20 px-4" style={{ background: 'var(--bg-page)' }}>
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="heading-2 mb-4">Simple, Transparent Pricing</h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
            No hidden charges. Pay once and own your website forever
          </p>
        </div>

        {/* Pricing Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {services.map((service) => (
            <div
              key={service.id}
              className={`relative bg-[var(--bg-card)] border-2 rounded-2xl p-6 transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 flex flex-col ${
                service.featured
                  ? 'border-[var(--accent-primary)] shadow-lg'
                  : 'border-[var(--border-light)] hover:border-[var(--accent-primary)]'
              }`}
            >
              {/* Featured Badge */}
              {service.featured && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <div
                    className="px-4 py-1.5 rounded-full flex items-center gap-1.5 shadow-md text-xs"
                    style={{ background: 'var(--gradient-button)' }}
                  >
                    <Star size={14} color="white" fill="white" />
                    <span className="text-white font-semibold">Popular</span>
                  </div>
                </div>
              )}

              {/* Price */}
              <div className="text-center mb-6 pt-2">
                <div
                  className="text-4xl font-bold mb-2"
                  style={{ color: 'var(--accent-text)' }}
                >
                  {service.price}
                </div>
                {service.priceNote && (
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    {service.priceNote}
                  </p>
                )}
              </div>

              {/* Title */}
              <h3 className="heading-3 text-center mb-4">{service.title}</h3>

              {/* Description */}
              <p
                className="body-medium text-center mb-6 text-sm"
                style={{ color: 'var(--text-secondary)' }}
              >
                {service.description}
              </p>

              {/* Features List */}
              <div className="flex-grow mb-6">
                <ul className="space-y-2.5">
                  {service.features.slice(0, 6).map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <Check
                        size={16}
                        className="mr-2 mt-0.5 flex-shrink-0"
                        style={{ color: 'var(--accent-text)' }}
                      />
                      <span
                        className="body-medium text-xs leading-relaxed"
                        style={{ color: 'var(--text-body)' }}
                      >
                        {feature}
                      </span>
                    </li>
                  ))}
                  {service.features.length > 6 && (
                    <li
                      className="text-xs font-semibold pt-2"
                      style={{ color: 'var(--accent-text)' }}
                    >
                      + {service.features.length - 6} more features
                    </li>
                  )}
                </ul>
              </div>

              {/* CTA Button */}
              <button
                onClick={scrollToContact}
                className={`w-full ${service.featured ? 'btn-primary' : 'btn-secondary'}`}
              >
                Choose Plan
              </button>
            </div>
          ))}
        </div>

        {/* Price Comparison Note */}
        <div className="bg-[var(--accent-wash)] border border-[var(--accent-primary)] rounded-2xl p-6 mb-12 max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <div>
              <div className="text-2xl font-bold mb-2" style={{ color: 'var(--accent-text)' }}>
                No Setup Fees
              </div>
              <p className="body-medium text-sm" style={{ color: 'var(--text-secondary)' }}>
                Pay only the service cost, nothing extra
              </p>
            </div>
            <div>
              <div className="text-2xl font-bold mb-2" style={{ color: 'var(--accent-text)' }}>
                Own Forever
              </div>
              <p className="body-medium text-sm" style={{ color: 'var(--text-secondary)' }}>
                No recurring payments for websites
              </p>
            </div>
            <div>
              <div className="text-2xl font-bold mb-2" style={{ color: 'var(--accent-text)' }}>
                Free Support
              </div>
              <p className="body-medium text-sm" style={{ color: 'var(--text-secondary)' }}>
                30 days post-launch assistance included
              </p>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center">
          <p className="body-large mb-6" style={{ color: 'var(--text-secondary)' }}>
            Ready to grow your business online? Let's get started today!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button onClick={scrollToContact} className="btn-primary">
              Get Free Consultation
            </button>
            <a
              href={`https://wa.me/9310085929?text=${encodeURIComponent(
                'Hi! I want to discuss pricing for your services.'
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary"
            >
              Chat on WhatsApp
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
